import { Button } from 'primereact/button';
import { FC, useState } from 'react';
import { motion } from 'motion/react';
import DashboardExitSlipModal from './DashboardExitSlipModal';
import { requestFullscreen } from '../../utils/fullscreen';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { ExitSlipData, ExitSlipResult } from '../../models/ExitSlip';
import {
  EXIT_SLIP_BANNER_LAYOUT_ID,
  EXIT_SLIP_BANNER_RADIUS,
  EXIT_SLIP_BANNER_TRANSITION,
} from './exitSlipBannerMotion';

interface Props {
  exitSlipsData: ExitSlipData | null;
  // Forwarded to the modal: the graded result, once the student finishes and closes it.
  onFinished: (result: ExitSlipResult[]) => void;
  // Whether this is the last ticket standing between the student and its skill (see
  // findSkillEarningExitSlipIds). Turns the banner into the promise that passing it earns the skill:
  // a sticker over the heading and the stacked-tickets icon in place of the plain one.
  isSkillEarning?: boolean;
}

const DashboardExitSlipBanner: FC<Props> = ({ exitSlipsData, onFinished, isSkillEarning = false }) => {
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const previewMode = usePreviewMode();
  return (
    <>
      {/* Shares its layout identity with the collapsed result card: when the student finishes and this
          banner unmounts, Motion morphs the card out of this box rather than hard-swapping them. */}
      <motion.div
        layoutId={EXIT_SLIP_BANNER_LAYOUT_ID}
        transition={EXIT_SLIP_BANNER_TRANSITION}
        style={{ borderRadius: EXIT_SLIP_BANNER_RADIUS }}
        className="nc_exit-slip-banner tw-flex-col md:tw-flex-row tw-pointer-events-auto"
        onClick={() => {
          // Guided tour walks this same screen without a real exit slip session — skip fullscreen.
          if (!previewMode) {
            requestFullscreen();
          }
          setVisibleModal(true);
        }}
      >
        <span className={`nc_exit-slip-banner-icon ${isSkillEarning ? 'nc_exit-slip-banner-icon--skill' : ''}`}></span>
        <div className="tw-flex tw-flex-col tw-items-center md:tw-items-start">
          {/* The sticker rides alongside the heading rather than being pinned over it: the banner is
              only 55% of the page wide, so an absolutely positioned one would hang off the edge on a
              long lesson name. Sitting in the flow it wraps under the heading instead. Its overlap
              with the heading is the negative margin in the stylesheet, not a transform. */}
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center md:tw-justify-start">
            <h3 className="tw-font-bold tw-text-xl lg:tw-text-2xl">Today’s exit ticket!</h3>
            {isSkillEarning && <span className="nc_exit-slip-banner-sticker">Chance to earn a skill!</span>}
          </div>
          <p className="tw-text-sm lg:tw-text-base tw-text-center md:tw-text-left">
            Lesson {exitSlipsData?.curriculumLesson.number}: {exitSlipsData?.curriculumLesson.name}
          </p>
        </div>
        {/* No own onClick: the div above already handles clicks, and this button is its descendant, so a
        second handler here would just double-fire on every click via bubbling. */}
        <Button label="Continue" className="md:!tw-ml-auto !tw-ml-[0px]" />
      </motion.div>
      {visibleModal && (
        <DashboardExitSlipModal
          visibleModal={visibleModal}
          setVisibleModal={setVisibleModal}
          exitSlipsData={exitSlipsData}
          onFinished={onFinished}
          isSkillEarning={isSkillEarning}
        />
      )}
    </>
  );
};

export default DashboardExitSlipBanner;
